import type { AccessTokenRequest } from 'tauri-plugin-auth0/bindings/AccessTokenRequest';
import type { AccessTokenResponse } from 'tauri-plugin-auth0/bindings/AccessTokenResponse';
import type { AuthSessionResponse } from 'tauri-plugin-auth0/bindings/AuthSessionResponse';
import type { BrowserKind } from 'tauri-plugin-auth0/bindings/BrowserKind';
import type { DownloadAttributionEvent } from 'tauri-plugin-auth0/bindings/DownloadAttributionEvent';
import type { LoginRequest } from 'tauri-plugin-auth0/bindings/LoginRequest';
import type { LoginResponse } from 'tauri-plugin-auth0/bindings/LoginResponse';
import type { LogoutRequest } from 'tauri-plugin-auth0/bindings/LogoutRequest';
import type { LogoutResponse } from 'tauri-plugin-auth0/bindings/LogoutResponse';
import { translateDesktopError } from '../errors';
import { isTauri } from '../utils';

const COMMAND_PREFIX = 'plugin:auth0|';

/** Shape of a serialized DesktopAuth0Error from the Rust plugin. */
type Auth0CommandError = {
  message: string;
  error_code?: string;
};

export type DesktopBrowserName = BrowserKind;

export type DesktopAuthLoginOptions = Partial<LoginRequest>;

/**
 * Unified auth session response.
 * Follows the Auth0 native SDK pattern where credentials include the access token.
 * Used for `auth_session` command and `auth:login-success` event.
 */
export type DesktopAuthSession = AuthSessionResponse;

/**
 * Payload of the `auth:download-attribution` event (SCR-5478). Deliberately
 * separate from `DesktopAuthSession` so the untrusted, opaque attribution
 * string never rides on the credential-bearing session response.
 */
export type DesktopDownloadAttributionPayload = DownloadAttributionEvent;

export type DesktopAuthLoginResponse = LoginResponse;

export type DesktopAuthGetTokenOptions = Partial<AccessTokenRequest>;

export type DesktopAuthTokenResponse = AccessTokenResponse;

export type DesktopAuthLogoutResponse = LogoutResponse;

export type DesktopAuthLogoutOptions = Partial<LogoutRequest>;

export type DesktopAuthDebugInfo = {
  session: {
    expiresAt: string;
    isValid: boolean;
    scope: string | null;
    tokenType: string | null;
    hasIdToken: boolean;
  } | null;
  pendingAttempt: {
    attemptId: string;
    state: string;
    hasNonce: boolean;
    authUrl: string;
    createdAtElapsedSecs: number;
    isExpired: boolean;
  } | null;
  hasRefreshToken: boolean;
  consumedAttemptsCount: number;
  keychainEnabled: boolean;
};

function isAuth0CommandError(value: unknown): value is Auth0CommandError {
  return typeof value === 'object' && value !== null && 'message' in value;
}

async function invokeAuth0Command<T>(
  command: string,
  payload?: Record<string, unknown>
): Promise<T> {
  if (!isTauri()) {
    throw new Error('Desktop Auth0 bridge is only available inside the Tauri runtime');
  }

  const { invoke } = await import('@tauri-apps/api/core');
  try {
    return await invoke<T>(`${COMMAND_PREFIX}${command}`, payload ?? {});
  } catch (error: unknown) {
    // Tauri serializes DesktopAuth0Error as { message, error_code }.
    // Translate the error_code to an i18n message before re-throwing.
    //
    // NOTE: Currently no caller displays the translated message to the user —
    // auth command errors are either silently caught (getAuthSession, setLogoutOnExit)
    // or logged without UI (logout). This translation is kept for forward-compatibility
    // so future callers can surface user-facing messages without extra work.
    if (isAuth0CommandError(error)) {
      const translated = translateDesktopError(error.message, error.error_code);
      throw new Error(translated);
    }
    throw error;
  }
}

export const desktopAuthBridge = {
  isAvailable(): boolean {
    return isTauri();
  },
  async login(options: DesktopAuthLoginOptions = {}): Promise<DesktopAuthLoginResponse> {
    return invokeAuth0Command<DesktopAuthLoginResponse>('auth_login', {
      request: options,
    });
  },
  async getAccessToken(
    options: DesktopAuthGetTokenOptions = {}
  ): Promise<DesktopAuthTokenResponse> {
    return invokeAuth0Command<DesktopAuthTokenResponse>('auth_get_access_token', {
      request: options,
    });
  },
  async logout(
    options: DesktopAuthLogoutOptions = {}
  ): Promise<DesktopAuthLogoutResponse> {
    return invokeAuth0Command<DesktopAuthLogoutResponse>('auth_logout', {
      request: options,
    });
  },
  async getAuthSession(): Promise<DesktopAuthSession> {
    return invokeAuth0Command<DesktopAuthSession>('auth_session');
  },
  /**
   * Set whether to automatically logout when the app exits.
   * This should be called when the feature flag value is known.
   */
  async setLogoutOnExit(enabled: boolean): Promise<void> {
    return invokeAuth0Command<void>('auth_set_logout_on_exit', { enabled });
  },
  /**
   * Get debug information about Rust-side storage state (non-production only)
   * This method should only be called in development/staging environments
   */
  async getDebugInfo(): Promise<DesktopAuthDebugInfo | null> {
    try {
      return await invokeAuth0Command<DesktopAuthDebugInfo>('auth_debug_info');
    } catch (error) {
      // Command may not be available in production, return null gracefully
      console.warn('[desktopAuthBridge.getDebugInfo] Command not available:', error);
      return null;
    }
  },
};

export { getDesktopAuthError } from './errorMap';
export type {
  DesktopAuthErrorAction,
  DesktopAuthErrorCode,
  DesktopAuthErrorContent,
  DesktopAuthErrorEventPayload,
} from './errorMap';

export { AUTH_EVENTS, setupAuthEventListeners } from './events';
export type { AuthEventHandlers, AuthEventName } from './events';
